import { useEffect, useRef, useState } from 'react';

interface SharePanelProps {
  title: string;
  open: boolean;
  onClose: () => void;
}

const platforms = [
  { icon: 'ri-message-3-line', label: '微信好友', color: 'bg-secondary-500' },
  { icon: 'ri-chat-smile-2-line', label: '朋友圈', color: 'bg-primary-600' },
  { icon: 'ri-links-line', label: '复制链接', color: 'bg-accent-500' },
  { icon: 'ri-qr-code-line', label: '生成二维码', color: 'bg-foreground-700' },
];

export default function SharePanel({ title, open, onClose }: SharePanelProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  if (!open) return null;

  const handleCopy = () => {
    setCopied(true);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).catch(() => undefined);
    }
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-foreground-950/50 animate-fade-in" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-background-50 rounded-t-lg sm:rounded-lg p-5 animate-fade-up">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-base font-semibold text-foreground-950">分享这条赛事</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors"
            aria-label="关闭"
          >
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{title}</p>

        <div className="grid grid-cols-4 gap-3">
          {platforms.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={item.label === '复制链接' ? handleCopy : undefined}
              className="flex flex-col items-center gap-2 group"
            >
              <span className={`w-12 h-12 rounded-lg ${item.color} text-background-50 flex items-center justify-center group-hover:opacity-90 transition-opacity`}>
                <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-lg`} />
              </span>
              <span className="text-xs text-foreground-600">{item.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between gap-3">
          <span className="text-xs text-foreground-500 truncate">
            {copied ? '链接已复制到剪贴板' : '分享功能仅为演示，不涉及登录与数据上传'}
          </span>
          {copied ? (
            <span className="flex items-center gap-1 text-xs text-secondary-600 whitespace-nowrap">
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center" />
              已完成
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}